import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const CrackCUETSection = () => (
  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 my-10">
    <h2 className="text-xl font-bold text-blue-900 mb-3">How Crack CUET Helps Get Better CUET Percentiles</h2>
    <p className="text-gray-700 leading-relaxed">
      Crack CUET is India's most focused CUET mock test platform, built by toppers from SRCC, St. Stephen's, and Hindu College. Our AI-powered system generates fresh, exam-realistic mock tests across all 15 CUET subjects — 50 questions per mock, +5/−1 marking, and a 60-minute timer that mirrors the real NTA exam. After every test, you receive a national percentile rank, topic-wise performance analytics, and detailed solutions so you know exactly what to revise. Our question engine covers every format tested in CUET — assertion-reason, match-the-following, case-based reading comprehension, and data interpretation — so you're never caught off guard on exam day. Whether you're targeting SRCC, St. Stephen's, or any of the 250+ universities that accept CUET scores, Crack CUET's structured mock practice consistently helps aspirants reach the 95th+ percentile.
    </p>
    <p className="mt-5 text-center">
      <Link to="/dashboard" className="inline-block bg-blue-600 text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-blue-700 transition-colors">
        Try a Free Mock Test — No Sign-up Required
      </Link>
    </p>
  </div>
);

export default function BlogCUET2026Guide() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>CUET 2026 Registration Dates, Fees & Exam Pattern — Complete Guide | Crack CUET</title>
        <meta name="description" content="Complete guide to CUET 2026: registration dates, application fees, exam pattern, syllabus, and everything you need to know before applying." />
        <meta name="keywords" content="CUET 2026 registration, CUET 2026 exam date, CUET 2026 fees, CUET exam pattern 2026, NTA CUET 2026" />
        <link rel="canonical" href="https://crackcuet.com/blog/cuet-2026-registration-dates-fees-exam-pattern" />
        <meta property="og:title" content="CUET 2026 Registration Dates, Fees & Exam Pattern — Complete Guide" />
        <meta property="og:description" content="Everything about CUET 2026 — registration dates, fees, exam pattern, and preparation tips." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://crackcuet.com/blog/cuet-2026-registration-dates-fees-exam-pattern" />
      </Helmet>
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center"><img src="/logo-full.png" alt="Crack CUET" className="h-16" /></Link>
          <Link to="/blog" className="text-sm text-gray-500 hover:text-gray-900">All Blogs</Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 py-12">
        <div className="mb-8">
          <span className="text-sm font-medium text-blue-600">Exam Info</span>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2 mb-4 leading-tight">
            CUET 2026: Registration Dates, Fees, Exam Date &amp; Complete Exam Pattern
          </h1>
          <div className="flex items-center gap-4 text-sm text-gray-400">
            <span>Mar 1, 2026</span>
            <span>10 min read</span>
            <span>By Crack CUET Team</span>
          </div>
        </div>

        <img
          src="https://images.unsplash.com/photo-1769794370969-eee773593604?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
          alt="Student preparing for CUET 2026 exam with books and notes"
          className="w-full rounded-xl mb-8 object-cover max-h-80"
        />

        <div className="prose prose-gray max-w-none text-gray-700 leading-relaxed space-y-6">
          <p>
            The <strong>Common University Entrance Test (CUET)</strong> has transformed the undergraduate admission landscape in India. For the Class of 2026, CUET is the single-window gateway to over <strong>250 central, state, deemed, and private universities</strong> — including Delhi University, JNU, BHU, Jamia Millia Islamia, and Hyderabad Central University. If you're a Class 12 student aiming for any of these institutions, understanding CUET 2026 inside-out is your first step.
          </p>

          <p>
            This guide covers everything: important dates, application fee, exam structure, marking scheme, eligibility criteria, and the universities you can target with your CUET score.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10">What is CUET 2026?</h2>
          <p>
            CUET (UG) 2026 is conducted by the <strong>National Testing Agency (NTA)</strong> for admission to undergraduate programmes. Unlike the traditional board-marks-based admission system, CUET gives every student — regardless of their state board or school — an equal, standardised platform to compete for seats at India's top universities. A student from a rural school in Bihar competes on equal footing with a student from a premium Delhi school. That's the promise of CUET.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10">CUET 2026 Important Dates (Expected)</h2>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-sm border-collapse">
              <thead>
                <tr className="bg-blue-50">
                  <th className="px-4 py-2 text-left font-semibold text-blue-800 border-b border-blue-200">Event</th>
                  <th className="px-4 py-2 text-left font-semibold text-blue-800 border-b border-blue-200">Expected Date</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Notification Release', 'February 2026'],
                  ['Online Registration Opens', 'February – March 2026'],
                  ['Last Date to Apply', 'March 2026'],
                  ['Admit Card Download', 'April 2026'],
                  ['CUET 2026 Exam Date', 'May 2026'],
                  ['Result Declaration', 'June 2026'],
                  ['DU Admission Rounds', 'July – August 2026'],
                ].map(([event, date], i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2 text-gray-800 border-b border-gray-100">{event}</td>
                    <td className="px-4 py-2 text-gray-600 border-b border-gray-100">{date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 italic">Note: Dates are tentative based on previous years' pattern. Always verify from the official NTA website (nta.ac.in).</p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10">CUET 2026 Application Fee</h2>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-sm border-collapse">
              <thead>
                <tr className="bg-blue-50">
                  <th className="px-4 py-2 text-left font-semibold text-blue-800 border-b border-blue-200">Category</th>
                  <th className="px-4 py-2 text-left font-semibold text-blue-800 border-b border-blue-200">Up to 3 Subjects</th>
                  <th className="px-4 py-2 text-left font-semibold text-blue-800 border-b border-blue-200">4–6 Subjects</th>
                  <th className="px-4 py-2 text-left font-semibold text-blue-800 border-b border-blue-200">7–10 Subjects</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['General / EWS', '₹750', '₹1,500', '₹1,750'],
                  ['OBC-NCL', '₹700', '₹1,400', '₹1,650'],
                  ['SC / ST / PwD / Third Gender', '₹650', '₹1,300', '₹1,550'],
                ].map(([cat, a, b, c], i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2 text-gray-800 border-b border-gray-100">{cat}</td>
                    <td className="px-4 py-2 text-gray-600 border-b border-gray-100">{a}</td>
                    <td className="px-4 py-2 text-gray-600 border-b border-gray-100">{b}</td>
                    <td className="px-4 py-2 text-gray-600 border-b border-gray-100">{c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-10">CUET 2026 Exam Pattern</h2>
          <p>CUET 2026 is divided into three sections:</p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6">Section IA &amp; IB — Languages</h3>
          <p>
            Section IA covers 13 scheduled Indian languages and Section IB covers 20 other languages (including foreign languages). You choose one or two languages based on your target university's requirements. Each language paper has <strong>50 questions (40 to attempt)</strong> in a 45-minute window.
          </p>
          <p>
            Reading comprehension, literary aptitude, and vocabulary form the bulk of language papers. For English (the most popular choice), passages are formal and academic in nature.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6">Section II — Domain-Specific Subjects</h3>
          <p>
            This is the most important section for your programme-specific admission. You can appear for up to <strong>6 domain subjects</strong>. Each paper has <strong>50 questions (40 to attempt)</strong> in 45 minutes. Domain papers are directly based on the <strong>NCERT Class 12 syllabus</strong> for that subject.
          </p>
          <p>Popular domain subjects and associated courses:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Economics</strong> → BA (Hons) Economics at SRCC, DSE, LSR</li>
            <li><strong>Business Studies + Accountancy</strong> → B.Com (Hons) at SRCC, Shri Ram, Kamala Nehru</li>
            <li><strong>History + Political Science</strong> → BA Hons at Hindu, Miranda House, Jesus & Mary</li>
            <li><strong>Biology + Chemistry + Physics</strong> → BSc at Hansraj, Ramjas, Deshbandhu</li>
            <li><strong>Mathematics</strong> → BSc (Hons) Maths, B.Tech admissions at IIIT Delhi</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6">Section III — General Test (GAT)</h3>
          <p>
            The General Test is mandatory for most programmes at DU and many central universities. It tests <strong>general aptitude, numerical ability, quantitative reasoning, logical and analytical reasoning, and general knowledge / current affairs</strong>. The paper has <strong>75 questions (60 to attempt)</strong> in 60 minutes.
          </p>
          <p>
            The GAT section often makes the difference between borderline candidates. Students from all streams compete for it — making preparation quality, not just academic background, the deciding factor.
          </p>

          <img
            src="https://images.unsplash.com/photo-1758685848122-64a287c1abde?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
            alt="Student writing notes while preparing for CUET exam"
            className="w-full rounded-xl my-8 object-cover max-h-72"
          />

          <h2 className="text-2xl font-bold text-gray-900 mt-10">CUET Marking Scheme 2026</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Correct answer:</strong> +5 marks</li>
            <li><strong>Incorrect answer:</strong> −1 mark (negative marking)</li>
            <li><strong>Unattempted question:</strong> 0 marks</li>
          </ul>
          <p>
            The marking scheme rewards accuracy over guessing. With 40 questions to attempt (out of 50), skipping uncertain questions is often the smarter strategy. This is exactly why practising with mock tests under timed conditions is essential — you learn when to skip and when to commit.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10">Eligibility for CUET 2026</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Class 12 appearing or passed:</strong> Students appearing in or who have passed Class 12 (or equivalent) from any recognised board are eligible</li>
            <li><strong>No age limit</strong> — NTA does not impose an age restriction for CUET (UG)</li>
            <li><strong>Subject eligibility</strong> varies by university — always check the specific programme requirements of your target institution</li>
            <li><strong>No minimum percentage</strong> required to apply for CUET, though individual universities may have minimum Class 12 cutoffs</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-10">Top Universities Accepting CUET 2026</h2>
          <p>Over 250 universities accept CUET scores. The most popular ones include:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Delhi University (DU)</strong> — all 91 colleges including SRCC, St. Stephen's, Hindu, Miranda House, LSR</li>
            <li><strong>Jawaharlal Nehru University (JNU)</strong></li>
            <li><strong>Banaras Hindu University (BHU)</strong></li>
            <li><strong>Jamia Millia Islamia</strong></li>
            <li><strong>Hyderabad Central University</strong></li>
            <li><strong>Pondicherry University</strong></li>
            <li><strong>Tezpur University, Assam University</strong>, and many more NE universities</li>
            <li><strong>Several deemed and private universities</strong> like Symbiosis, Sharda, Bennett</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-10">How to Register for CUET 2026 — Step by Step</h2>
          <ol className="list-decimal pl-6 space-y-2">
            <li>Visit the official NTA website: <strong>cuet.samarth.ac.in</strong></li>
            <li>Click on "New Registration" and create your account</li>
            <li>Fill in personal details — name, DOB, Class 12 board, marks</li>
            <li>Select your test languages, domain subjects, and General Test preference</li>
            <li>Choose exam city preferences (up to 4 cities)</li>
            <li>Upload photo, signature, and Class 10 certificate</li>
            <li>Pay the application fee online</li>
            <li>Download and save the confirmation page</li>
          </ol>
          <p>
            <strong>Pro tip:</strong> Apply early. NTA typically releases the admit card 2–3 weeks before the exam, and late applications risk losing preferred exam cities.
          </p>

          <CrackCUETSection />

          <h2 className="text-2xl font-bold text-gray-900 mt-10">Bottom Line</h2>
          <p>
            CUET 2026 is your best shot at getting into a top central university on merit, regardless of your school background. The exam rewards consistent preparation, understanding of NCERT content, and — critically — time management under pressure. Start your preparation early, take enough mock tests to build speed, and track your performance across subjects. The students who score in the 99th percentile aren't necessarily the most academically gifted — they're the most consistently prepared.
          </p>
        </div>
      </article>
    </div>
  );
}
